import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../../ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from '../../ui/sheet';
import { Button } from '../../ui/button';
import { Globe, Menu, Search } from 'lucide-react';
import LanguageSelector from '../../LanguageSelector';
import NavMenuItem from './NavMenuItem';
import MobileSearchField from './MobileSearchField';

/**
 * Guest navigation (not logged in): Explore dropdown/sheet + Log in + Join Now.
 */
export default function GuestNav({ guestAccessibleItems, mobileOpen, setMobileOpen, isActive }) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center gap-2">
      {/* Desktop "Explore" dropdown */}
      <NavigationMenu className="hidden md:flex">
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger
              className="bg-white/10 text-white border border-white/25 hover:bg-white/20 data-[state=open]:bg-white/25"
              data-testid="nav-guest-menu"
            >
              <Globe className="w-4 h-4 mr-2" />
              Explore
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="p-4 w-[500px]">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                  <Globe className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Open to Everyone</span>
                </div>
                <ul className="grid grid-cols-2 gap-1">
                  {guestAccessibleItems.map((item) => (
                    <li key={item.href}>
                      <NavigationMenuLink asChild>
                        <NavMenuItem item={item} />
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      {/* Mobile guest sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild className="md:hidden">
          <Button
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/25 text-white hover:bg-white/20 hover:text-white"
            data-testid="mobile-guest-menu-trigger"
          >
            <Menu className="h-5 w-5 mr-1" />
            Explore
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-80 overflow-y-auto">
          <nav className="flex flex-col gap-1 mt-8">
            <MobileSearchField onSubmit={() => setMobileOpen(false)} />
            <div className="px-4 pb-4 mb-2 border-b">
              <p className="text-xs text-muted-foreground mb-2">Language</p>
              <LanguageSelector variant="default" showLabel={true} />
            </div>
            <div className="flex items-center gap-2 px-4 py-2 mb-2">
              <Globe className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Open to Everyone</span>
            </div>
            {guestAccessibleItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-emerald-600 text-white'
                      : 'hover:bg-emerald-50 text-emerald-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
            <div className="mt-6 pt-4 border-t px-4 space-y-2">
              <Button
                className="w-full btn-primary"
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/register');
                }}
                data-testid="mobile-register"
              >
                Join Now
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/login');
                }}
                data-testid="mobile-login"
              >
                Log in
              </Button>
            </div>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Language selector is rendered once in the outer Navbar right section — do not duplicate here. */}
      <Button
        variant="ghost"
        size="icon"
        className="hidden md:flex lg:hidden text-white hover:bg-white/10 hover:text-white"
        onClick={() => navigate('/search')}
        data-testid="nav-guest-search"
        aria-label="Search"
        title="Search"
      >
        <Search className="w-5 h-5" />
      </Button>
      <Button
        variant="ghost"
        onClick={() => navigate('/login')}
        data-testid="nav-login"
        className="text-white hover:bg-white/10 hover:text-white"
      >
        Log in
      </Button>
      <Button
        className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm"
        onClick={() => navigate('/register')}
        data-testid="nav-register"
      >
        Join Now
      </Button>
    </div>
  );
}
